import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import ethereumService from '../services/ethereumService';

interface TokenBalance {
  symbol: string;
  balance: string;
  contractAddress: string;
  decimals: number;
}

interface VoiceTransaction {
  from: string;
  to: string;
  amount: string;
  timestamp: Date;
  type: string;
  hash: string;
}

interface Web3WalletContextType {
  isConnected: boolean;
  address: string | null;
  balance: string;
  tokenBalances: TokenBalance[];
  chainId: number | null;
  connect: () => Promise<void>;
  disconnect: () => void;
  switchToBNBChain: () => Promise<void>;
  isConnecting: boolean;
  error: string | null;
  // Voice Token specific functions
  claimVoiceAirdrop: (claimCode: string, tier: number, signature: string) => Promise<{ success: boolean; hash?: string; error?: string }>;
  getVoiceTransactions: () => Promise<VoiceTransaction[]>;
  hasClaimedAirdrop: () => Promise<boolean>;
  getVoiceAirdropStats: () => Promise<any>;
  getVoiceGasFee: () => Promise<string>;
  voiceTransactions: VoiceTransaction[];
}

const Web3WalletContext = createContext<Web3WalletContextType | undefined>(undefined);

const BNB_CHAIN_CONFIG = {
  chainId: '0x38', // 56 in hex
  chainName: 'BNB Smart Chain',
  nativeCurrency: {
    name: 'BNB',
    symbol: 'BNB',
    decimals: 18,
  },
  rpcUrls: ['https://bsc-dataseed1.bnbchain.org'],
  blockExplorerUrls: ['https://bscscan.com'],
};

// Common BEP-20 tokens on BNB Smart Chain
const COMMON_TOKENS = [
  {
    symbol: 'VOICE',
    contractAddress: '0x3c027958a3D4C4eDb008ee319692F7D387e6C5fB', // VOICE token contract address
    decimals: 18
  },
  {
    symbol: 'USDT',
    contractAddress: '0x55d398326f99059fF775485246999027B3197955',
    decimals: 18
  },
  {
    symbol: 'USDC',
    contractAddress: '0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d',
    decimals: 18
  },
  {
    symbol: 'BUSD',
    contractAddress: '0xe9e7CEA3DedcA5984780Bafc599bD69ADd087D56',
    decimals: 18
  }
];

interface Web3WalletProviderProps {
  children: ReactNode;
}

export const Web3WalletProvider: React.FC<Web3WalletProviderProps> = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [address, setAddress] = useState<string | null>(null);
  const [balance, setBalance] = useState('0');
  const [tokenBalances, setTokenBalances] = useState<TokenBalance[]>([]);
  const [chainId, setChainId] = useState<number | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [voiceTransactions, setVoiceTransactions] = useState<VoiceTransaction[]>([]);

  // Check if wallet is already connected
  useEffect(() => {
    checkConnection();
  }, []);

  const checkConnection = async () => {
    if (typeof window.ethereum !== 'undefined') {
      try {
        const accounts = await window.ethereum.request({ method: 'eth_accounts' });
        if (accounts.length > 0) {
          setAddress(accounts[0]);
          setIsConnected(true);
          await updateBalance(accounts[0]);
          await updateChainId();
        }
      } catch (error) {
        console.error('Error checking connection:', error);
      }
    }
  };

  const updateBalance = async (walletAddress: string) => {
    if (typeof window.ethereum !== 'undefined') {
      try {
        const balance = await window.ethereum.request({
          method: 'eth_getBalance',
          params: [walletAddress, 'latest'],
        });
        // Convert from wei to BNB
        const balanceInBNB = parseInt(balance, 16) / Math.pow(10, 18);
        setBalance(balanceInBNB.toFixed(6));
        
        // Update token balances
        await updateTokenBalances(walletAddress);
      } catch (error) {
        console.error('Error getting balance:', error);
        setBalance('0');
      }
    }
  };

  const updateTokenBalances = async (walletAddress: string) => {
    if (typeof window.ethereum !== 'undefined') {
      try {
        const balances: TokenBalance[] = [];
        
        for (const token of COMMON_TOKENS) {
          if (token.contractAddress === '0x0000000000000000000000000000000000000000') {
            // Skip placeholder VOICE token for now
            continue;
          }
          
          try {
            // ERC-20 balanceOf function call
            const data = `0x70a08231000000000000000000000000${walletAddress.slice(2)}`;
            const result = await window.ethereum.request({
              method: 'eth_call',
              params: [{
                to: token.contractAddress,
                data: data
              }, 'latest']
            });
            
            if (result && result !== '0x') {
              const balance = parseInt(result, 16) / Math.pow(10, token.decimals);
              if (balance > 0) {
                balances.push({
                  symbol: token.symbol,
                  balance: balance.toFixed(6),
                  contractAddress: token.contractAddress,
                  decimals: token.decimals
                });
              }
            }
          } catch (tokenError) {
            console.error(`Error getting ${token.symbol} balance:`, tokenError);
          }
        }
        
        setTokenBalances(balances);
      } catch (error) {
        console.error('Error updating token balances:', error);
      }
    }
  };

  const updateChainId = async () => {
    if (typeof window.ethereum !== 'undefined') {
      try {
        const chainId = await window.ethereum.request({ method: 'eth_chainId' });
        setChainId(parseInt(chainId, 16));
      } catch (error) {
        console.error('Error getting chain ID:', error);
      }
    }
  };

  const connect = async () => {
    if (typeof window.ethereum === 'undefined') {
      setError('MetaMask is not installed. Please install MetaMask to continue.');
      return;
    }

    setIsConnecting(true);
    setError(null);

    try {
      // Request account access
      const accounts = await window.ethereum.request({
        method: 'eth_requestAccounts',
      });

      if (accounts.length > 0) {
        setAddress(accounts[0]);
        setIsConnected(true);
        await updateBalance(accounts[0]);
        await updateChainId();
        
        // Check if we're on BNB Smart Chain
        const currentChainId = await window.ethereum.request({ method: 'eth_chainId' });
        if (currentChainId !== BNB_CHAIN_CONFIG.chainId) {
          await switchToBNBChain();
        }
      }
    } catch (error: any) {
      console.error('Error connecting wallet:', error);
      setError(error.message || 'Failed to connect wallet');
    } finally {
      setIsConnecting(false);
    }
  };

  const disconnect = () => {
    setIsConnected(false);
    setAddress(null);
    setBalance('0');
    setTokenBalances([]);
    setChainId(null);
    setError(null);
  };

  const switchToBNBChain = async () => {
    if (typeof window.ethereum === 'undefined') {
      setError('MetaMask is not installed');
      return;
    }

    try {
      // Try to switch to BNB Smart Chain
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: BNB_CHAIN_CONFIG.chainId }],
      });
    } catch (switchError: any) {
      // If the chain hasn't been added to MetaMask, add it
      if (switchError.code === 4902) {
        try {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [BNB_CHAIN_CONFIG],
          });
        } catch (addError: any) {
          setError('Failed to add BNB Smart Chain to MetaMask');
          console.error('Error adding chain:', addError);
        }
      } else {
        setError('Failed to switch to BNB Smart Chain');
        console.error('Error switching chain:', switchError);
      }
    }
  };

  // Listen for account and chain changes
  useEffect(() => {
    if (typeof window.ethereum !== 'undefined') {
      const handleAccountsChanged = (accounts: string[]) => {
        if (accounts.length === 0) {
          disconnect();
        } else {
          setAddress(accounts[0]);
          updateBalance(accounts[0]);
        }
      };

      const handleChainChanged = (chainId: string) => {
        setChainId(parseInt(chainId, 16));
        if (address) {
          updateBalance(address);
        }
      };

      window.ethereum.on('accountsChanged', handleAccountsChanged);
      window.ethereum.on('chainChanged', handleChainChanged);

      return () => {
        window.ethereum?.removeListener('accountsChanged', handleAccountsChanged);
        window.ethereum?.removeListener('chainChanged', handleChainChanged);
      };
    }
  }, [address]);

  // Voice Token specific functions
  const claimVoiceAirdrop = async (claimCode: string, tier: number, signature: string) => {
    try {
      if (!address) {
        throw new Error('Wallet not connected');
      }
      
      const result = await ethereumService.claimVoiceAirdrop(claimCode, tier, signature);
      
      if (result.success) {
        // Refresh balances and transactions after successful claim
        await updateBalance(address);
        await loadVoiceTransactions();
      }
      
      return result;
    } catch (error: any) {
      console.error('Failed to claim Voice airdrop:', error);
      return {
        success: false,
        error: error.message
      };
    }
  };

  const getVoiceTransactions = async (): Promise<VoiceTransaction[]> => {
    try {
      if (!address) {
        return [];
      }
      
      const transactions = await ethereumService.getVoiceTransactionHistory(address);
      setVoiceTransactions(transactions);
      return transactions;
    } catch (error) {
      console.error('Failed to get Voice transactions:', error);
      return [];
    }
  };

  const hasClaimedAirdrop = async (): Promise<boolean> => {
    try {
      if (!address) {
        return false;
      }
      
      return await ethereumService.hasClaimedAirdrop(address);
    } catch (error) {
      console.error('Failed to check airdrop claim status:', error);
      return false;
    }
  };

  const getVoiceAirdropStats = async () => {
    try {
      return await ethereumService.getVoiceAirdropStats();
    } catch (error) {
      console.error('Failed to get airdrop stats:', error);
      return null;
    }
  };

  const getVoiceGasFee = async (): Promise<string> => {
    try {
      return await ethereumService.getVoiceGasFee();
    } catch (error) {
      console.error('Failed to get Voice gas fee:', error);
      return '0';
    }
  };

  const loadVoiceTransactions = async () => {
    if (address) {
      await getVoiceTransactions();
    }
  };

  // Load Voice transactions when address changes
  useEffect(() => {
    loadVoiceTransactions();
  }, [address]);

  // Listen to Voice Token events
  useEffect(() => {
    if (isConnected && address) {
      ethereumService.listenToVoiceEvents((event) => {
        console.log('Voice Token event:', event);
        
        // Refresh transactions when new events are received
        if (event.type === 'TransactionRecorded' || event.type === 'AirdropClaimed') {
          loadVoiceTransactions();
          
          // Also refresh balances
          updateBalance(address);
        }
      });
    }
  }, [isConnected, address]);

  const value: Web3WalletContextType = {
    isConnected,
    address,
    balance,
    tokenBalances,
    chainId,
    connect,
    disconnect,
    switchToBNBChain,
    isConnecting,
    error,
    // Voice Token functions
    claimVoiceAirdrop,
    getVoiceTransactions,
    hasClaimedAirdrop,
    getVoiceAirdropStats,
    getVoiceGasFee,
    voiceTransactions,
  };

  return (
    <Web3WalletContext.Provider value={value}>
      {children}
    </Web3WalletContext.Provider>
  );
};

export const useWeb3Wallet = (): Web3WalletContextType => {
  const context = useContext(Web3WalletContext);
  if (context === undefined) {
    throw new Error('useWeb3Wallet must be used within a Web3WalletProvider');
  }
  return context;
};